/**
 * Die Übernahme einer Anfrage in ein Angebot — KI-Weg (`POST /api/inquiries/:id/komponieren`)
 * und manueller Weg (`POST /api/inquiries/:id/manuell-uebernehmen`), samt Abbruch, KI-Fortschritt
 * und der vollständigen, über Monate gewachsenen Fehlerbehandlung.
 *
 * ## Warum als Hook
 *
 * Arbeitspaket F1 (`docs/PLAN-ANFRAGE-VORGANG-EINE-IDENTITAET.md` Teil B2): ab sofort führt jeder
 * Fall auf DIESELBE Vorgangsseite (`pages/event-detail.tsx`) — auch ein Fall, der noch kein
 * Angebot hat. Die beiden Erzeugen-Aktionen lebten bis hierher AUSSCHLIESSLICH auf
 * `pages/anfrage-detail.tsx`; ein Betreiber, der über die Vorgangsseite kommt, hatte dort keinen
 * Weg vom „noch kein Angebot"-Leerzustand zum Angebot. Die Logik ist hier EINMAL extrahiert und
 * wird von BEIDEN Seiten aufgerufen, statt sie zu verdoppeln — zwei Kopien wären zwei
 * Fehlerbehandlungen, und beide sähen für sich plausibel aus.
 *
 * Reines Refactoring an der Quelle: jeder Fehlertext, jede Bedingung und die Reihenfolge der
 * Zweige sind wortwörtlich aus `pages/anfrage-detail.tsx` übernommen (Git-Historie dort).
 *
 * ## Der EINE echte Unterschied zwischen den beiden Aufrufern
 *
 * Was nach dem Erfolg passiert: die Anfrage-Detailseite WECHSELT zur Vorgangsseite/zum Builder
 * (`navigate(angebotPfad(res.eventId))`), die Vorgangsseite ist bereits dort und lädt nur neu.
 * Beides steckt in `onErfolg` — der Hook entscheidet das nicht, er meldet nur die `eventId`.
 * (Backend-seitig ist das dieselbe `eventId`: `komponieren`/`manuell-uebernehmen` BEFÜLLEN einen
 * bereits bestehenden Vorgang, statt einen zweiten anzulegen — s. `ladeBestehendenVorgang` in
 * `apps/api/src/routes/inquiries.ts`.)
 *
 * ## Kein Copilot-, kein Router-Zugriff hier
 *
 * `reportContext` kommt als optionaler Callback herein statt über `useCopilot()`. Der Hook
 * bliebe sonst an den `CopilotProvider` gebunden und jede Seite/jeder Test, der ihn benutzt,
 * müsste diesen Provider mitbringen — für die Vorgangsseite, die gar keine Kontextmeldung
 * schickt, wäre das eine Kopplung ohne Gegenwert.
 */
import { useEffect, useRef, useState } from "react";
import { apiMutate } from "./useApi";
import type { ApiError } from "../lib/api";
import type { ReportContextPayload } from "./useAgentSession";

export interface AngebotUebernahmeOptionen {
  /**
   * Was nach erfolgreicher Übernahme geschieht — bekommt die `eventId` des (jetzt befüllten)
   * Vorgangs. Anfrage-Detailseite: `navigate(angebotPfad(id))`. Vorgangsseite: Daten neu laden
   * (man steht schon auf genau diesem Vorgang, `id === event.id`).
   */
  onErfolg: (eventId: string) => void;
  /**
   * Dedup-Plan Phase 3: der im Dubletten-Hinweis EXPLIZIT gewählte Bestandskunde. Ohne Auswahl
   * bleibt der Body leer und das Verhalten exakt das bisherige (neuer Kunde bei fehlendem
   * E-Mail-Treffer). Serverseitig validiert (409 bei Konflikt/anonymisiert).
   */
  uebernahmeKundeId?: string | null;
  /** Quelldaten neu laden — nach Abbruch, nach `generation_in_progress` und während des Pollings. */
  onReload?: () => void;
  /**
   * `inquiries.generation_phase` der Anfrage, soweit die aufrufende Seite sie kennt. Treibt die
   * Schrittanzeige des Fortschritts-Modals (`thinking` → 1, `writing` → 2). Fehlt sie (die
   * Vorgangsseite lädt die Anfragezeile nicht), bleibt die Anzeige auf Schritt 0 stehen, bis der
   * Erfolg auf 3 springt — der Ablauf selbst ändert sich dadurch nicht.
   */
  generationPhase?: string | null;
  /** Optionale Copilot-Kontextmeldung (Slice 3, §6.4). Ohne Callback passiert schlicht nichts. */
  onReportContext?: (payload: ReportContextPayload) => void;
}

export interface AngebotUebernahme {
  /** ✨ Herzstück: Anfrage → KI-Angebotsentwurf. */
  generateOffer: () => Promise<void>;
  /** Sekundärer Weg: Vorgang + GENAU EINE leere Angebotsvariante, ohne Modellaufruf. */
  createManually: () => Promise<void>;
  /** Bricht einen laufenden KI-Lauf ab (Client-Abort + serverseitiger Anspruchs-Verzicht). */
  cancelGenerate: () => void;
  generating: boolean;
  genStep: number;
  creatingManually: boolean;
  formError: string | null;
  setFormError: (meldung: string | null) => void;
}

export function useAngebotUebernahme(
  inquiryId: string | null,
  opts: AngebotUebernahmeOptionen,
): AngebotUebernahme {
  const [formError, setFormError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);
  const [genStep, setGenStep] = useState(0);
  // Betreiber-Wunsch (Antoine, System-Architektur-Entscheidung bereits abgestimmt): "Kann ich
  // auch eine manuelle Angebot erstellen? Oder muss ich immer KI-Angebot erzeugen?" — der
  // sekundaere Weg neben `generateOffer`, kein Fortschritts-Popup (das ist KI-spezifisch), nur
  // ein normaler Busy-Zustand.
  const [creatingManually, setCreatingManually] = useState(false);
  const generateAbortRef = useRef<AbortController | null>(null);
  const lastReportedFailureInquiryIdRef = useRef<string | null>(null);

  // Die Callbacks liegen in Refs, damit der Poll-Effekt unten nicht an ihrer Identitaet haengt
  // (ein bei jedem Render neu gebautes `onReload` wuerde das Intervall sonst staendig neu
  // aufsetzen). Dieselbe Absicht wie der eslint-disable-Kommentar an der Vorlage in
  // `pages/anfrage-detail.tsx`, nur ohne die Ausnahme zu brauchen.
  const optsRef = useRef(opts);
  optsRef.current = opts;

  /** ✨ Herzstueck: Anfrage -> KI-Angebotsentwurf (unveraendert aus `pages/anfrage-detail.tsx`). */
  async function generateOffer(): Promise<void> {
    if (!inquiryId) return;
    const controller = new AbortController();
    generateAbortRef.current = controller;
    setGenerating(true);
    setGenStep(0);
    setFormError(null);
    try {
      const res = await apiMutate<{ eventId: string; variantenAnzahl: number }>(
        `/api/inquiries/${inquiryId}/komponieren`,
        "POST",
        optsRef.current.uebernahmeKundeId ? { customerId: optsRef.current.uebernahmeKundeId } : {},
        controller.signal,
      );
      setGenStep(3);
      if (lastReportedFailureInquiryIdRef.current === inquiryId) {
        lastReportedFailureInquiryIdRef.current = null;
        optsRef.current.onReportContext?.({
          surface: "offer",
          action: "angebot_komponieren",
          errorCode: "komposition_erfolgreich",
          outcome: "success",
        });
      }
      optsRef.current.onErfolg(res.eventId);
      // Anders als in der Drawer-/Anfrage-Fassung (die sofort wegnavigiert und deshalb nie
      // zuruecksetzen musste) bleibt die Vorgangsseite stehen — ohne diese Zeile bliebe ihr
      // Fortschritts-Modal fuer immer offen. Fuer die Anfrage-Detailseite ist der Effekt
      // unsichtbar: `onErfolg` navigiert, beide State-Updates laufen im selben Batch.
      setGenerating(false);
    } catch (err) {
      setGenerating(false);
      if (err instanceof DOMException && err.name === "AbortError") {
        optsRef.current.onReload?.();
        return;
      }
      const msg = err instanceof Error ? err.message : String(err);
      const code = err instanceof Error && "code" in err ? (err as ApiError).code : undefined;
      if (/generation_in_progress|laeuft bereits|läuft bereits/i.test(msg)) {
        setFormError("Für diese Anfrage läuft die Angebotserzeugung bereits (anderer Tab oder vorheriger Versuch). Bitte kurz warten und die Seite neu laden.");
        optsRef.current.onReload?.();
      } else if (/ai_failed|fehlgeschlagen.*KEIN Entwurf/i.test(msg)) {
        setFormError("Die KI-Analyse ist fehlgeschlagen – es wurde kein Entwurf angelegt. Bitte erneut versuchen.");
      } else if (/ai_not_configured/i.test(msg)) {
        setFormError("Kein KI-Zugang konfiguriert – der Angebotsentwurf braucht die KI-Analyse (siehe Einstellungen → System).");
      } else if (code === "guests_missing" || /guests_missing|Gaestezahl/i.test(msg)) {
        // Antoine, 02.09.2026: Der rohe Server-Guard ("Ohne Gaestezahl laesst sich kein Angebot
        // rechnen.") blockiert scheinbar die gesamte Angebotserstellung — dabei ist das nur eine
        // KI-spezifische Anforderung (Mengenplanung pro Kopf). Der gaestezahl-freie manuelle Weg
        // (`createManually`, PR #845) steht daneben immer zur Verfuegung. Fehlermeldung verweist
        // jetzt aktiv darauf, statt den Betreiber vor einer scheinbaren Sackgasse stehen zu lassen.
        setFormError(
          "Für die KI-Angebotserstellung wird eine Gästezahl benötigt. Trag sie ein, oder erstelle das Angebot manuell und ergänze die Zahl später.",
        );
      } else {
        setFormError(
          /empty_catalog|leer/i.test(msg)
            ? "Dein Katalog ist noch leer. Lege zuerst Positionen an oder importiere eine Speisekarte (Katalog → Import)."
            : msg,
        );
      }
      if (code === "empty_catalog" || code === "no_positions_generated") {
        lastReportedFailureInquiryIdRef.current = inquiryId;
        optsRef.current.onReportContext?.({ surface: "offer", action: "angebot_komponieren", errorCode: code });
      }
    } finally {
      generateAbortRef.current = null;
    }
  }

  /**
   * Sekundaerer Weg neben `generateOffer`: legt Event + GENAU EINE leere Angebotsvariante an
   * (`POST /api/inquiries/:id/manuell-uebernehmen`) — kein Modellaufruf, deshalb auch kein
   * Fortschritts-Popup/`genStep`, nur der normale Busy-Zustand (Knopf deaktiviert). Nutzt
   * denselben `uebernahmeKundeId`-Bestandskunden wie das KI-Angebot.
   */
  async function createManually(): Promise<void> {
    if (!inquiryId) return;
    setCreatingManually(true);
    setFormError(null);
    try {
      const res = await apiMutate<{ eventId: string }>(
        `/api/inquiries/${inquiryId}/manuell-uebernehmen`,
        "POST",
        optsRef.current.uebernahmeKundeId ? { customerId: optsRef.current.uebernahmeKundeId } : {},
      );
      optsRef.current.onErfolg(res.eventId);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : String(err));
    } finally {
      setCreatingManually(false);
    }
  }

  function cancelGenerate(): void {
    if (!inquiryId) return;
    generateAbortRef.current?.abort();
    void apiMutate(`/api/inquiries/${inquiryId}/erzeugung-abbrechen`, "POST", {})
      .catch(() => {})
      .finally(() => optsRef.current.onReload?.());
  }

  // Solange die KI laeuft, wird die Quelle nachgeladen — daraus kommt die Phase fuer die
  // Schrittanzeige (s. naechster Effekt).
  useEffect(() => {
    if (!generating || !inquiryId) return;
    const iv = setInterval(() => optsRef.current.onReload?.(), 700);
    return () => clearInterval(iv);
  }, [generating, inquiryId]);

  useEffect(() => {
    if (!generating || !inquiryId) return;
    const phase = opts.generationPhase ?? null;
    setGenStep(phase === "writing" ? 2 : phase === "thinking" ? 1 : 0);
  }, [opts.generationPhase, generating, inquiryId]);

  return { generateOffer, createManually, cancelGenerate, generating, genStep, creatingManually, formError, setFormError };
}
